import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import FormButton from '@ui/button/FormButton';
import useWebStorage, { PERSISTED_STATE_STORAGE_KEYS } from '@app/component/web-storage/useWebStorage';

// The Full width choice of the BI reports: one choice for every report, kept by the browser per device (a laptop and a
// wide monitor need different choices). The pattern of the Date based rates table (ROO2-127), with one change: the BI
// rail takes 96 px, so the limit of 1,280 px (max-w-7xl) binds only from a 1,376 px window. The switch shows and acts
// from there; below it, the switch would change nothing.
type FullWidth = { isFullWidth: boolean, toggle: () => void };

const BiFullWidthContext = createContext<FullWidth>({ isFullWidth: false, toggle: () => undefined });

export const BiFullWidthProvider: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
	const storage = useWebStorage();
	const [isFullWidth, setIsFullWidth] = useState<boolean>(() => {
		try {
			return storage.getItem<boolean>(PERSISTED_STATE_STORAGE_KEYS.BI_FULL_WIDTH) === true;
		} catch {
			return false;
		}
	});
	const toggle = useCallback(() => setIsFullWidth(current => {
		const next = !current;
		try {
			storage.setItem(PERSISTED_STATE_STORAGE_KEYS.BI_FULL_WIDTH, next);
		} catch {
			// Keep the visit usable when browser storage is unavailable.
		}
		return next;
	}), [storage]);
	const value = useMemo(() => ({ isFullWidth, toggle }), [isFullWidth, toggle]);
	return <BiFullWidthContext.Provider value={value}>{children}</BiFullWidthContext.Provider>;
};

export const useBiFullWidth = () => useContext(BiFullWidthContext);

// The width of the header and the content of a BI report. Both use the same class, so their edges stay in one line.
export const useBiWidthClass = () => useBiFullWidth().isFullWidth ? 'mx-auto max-w-7xl min-[1376px]:max-w-none' : 'mx-auto max-w-7xl';

// The switch in the page header of a BI report. The label names the state, as on the rates table.
export const BiFullWidthButton = () => {
	const { isFullWidth, toggle } = useBiFullWidth();
	return <FormButton state="white" extraClassName="hidden min-[1376px]:inline-flex font-normal py-1.5" onClick={toggle}
		icon={isFullWidth ? <ArrowsPointingInIcon className="h-4 w-4" aria-hidden="true" /> : <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />}>
		{isFullWidth ? 'Default width' : 'Full width'}
	</FormButton>;
};
